/**
 * AUTOMATION SIMULATION — Pagina "dry-run": quali record l'automazione
 * intercetterebbe, su un campione degli ultimi N. Ci si arriva dal bottone
 * "Simula" del builder (AutomationForm).
 *
 * FLUSSO: all'apertura si vede la sola testata — nome, stato, categoria, campo
 * "record da esaminare", "Esegui simulazione". I risultati compaiono dopo il run
 * e restano: ritoccare il campo non li tocca, ed è l'hero a dire su quanti
 * record sono stati prodotti. Attivare l'automazione non si fa da qui: è
 * un'azione sul record e vive sulla sua view.
 *
 * IL CAMPIONE è dei record più RECENTI (createDate desc), non casuale, e la
 * selezione NON dipende dall'evento del QUANDO: rigiocare un evento nel passato
 * non è calcolabile: modifiedDate conserva solo l'ultima modifica, i record
 * cancellati non esistono più, e nessuna entità conserva le proprie versioni.
 * Ne segue che le condizioni si valutano sullo stato ATTUALE e che
 * un'automazione pensata per record vecchi può non intercettare nulla su un
 * campione piccolo — cosa diversa da un'automazione che non funziona.
 *
 * STATO: vengono dal server la testata, l'hero, tutte le stat, il pannello
 * delle azioni a destra e la TABELLA — colonne, righe, valori letti, esito di
 * ogni condizione e, sugli esclusi, quali condizioni sono cadute e con quale
 * valore. Del mockup (crm-mockup.html, schermata id="simulation") non resta
 * più nulla di finto: anche il pannello di avviso è alimentato dai dati veri.
 *
 * Quel pannello NON chiede niente al server. La risposta della simulazione
 * porta già l'esito di ogni condizione su ogni record esaminato, e l'avviso è
 * un raggruppamento di quella matrice: la tabella la legge per RIGA ("perché
 * questo record è escluso"), il pannello per COLONNA ("quale condizione dice
 * di no più spesso"). Due domande diverse sugli stessi dati, e la seconda su
 * un campione grande non si risponde scorrendo le righe.
 *
 * NON c'è un pannello "errori di configurazione", e la sua assenza è una scelta
 * da non revocare leggendo il mockup. Le regole che lo alimenterebbero le
 * controlla già AutomationDefinitionValidator — campo filtrabile, operatore
 * valido e applicabile al tipo, origine e forma del valore — che è lo STESSO
 * validatore della pubblicazione; e qui non si arriva senza il suo verde,
 * perché "Simula" resta spento finché la configurazione non è validata e
 * allineata al DB (`canActOnRecord`, in AutomationForm). Il cancello vale su
 * ENTRAMBE le porte, non solo sull'editor: la view in sola lettura è lo stesso
 * componente, e l'effetto che chiama /validate non ha guardie sulla modalità.
 * Una configurazione rotta non arriva quindi a questa schermata — la nomina il
 * pannello Validazione, che è dove la si corregge.
 * Chi apre /simulation SENZA passare di lì — URL digitato a mano, segnalibro
 * riaperto — non scavalca però nulla: la pagina RIFÀ la domanda per conto
 * proprio, all'apertura, con `getValidation`. Che prende un id e non un payload,
 * quindi giudica il record che la simulazione poi leggerebbe e non una
 * ricostruzione client di quel record — distinzione che il builder può
 * permettersi di ignorare, perché il payload che valida è esattamente quello che
 * sta per salvare.
 * Fuori dal cancello resta il solo client che chiami l'endpoint di simulazione
 * direttamente, fuori dalla UI: quella chiusura sarebbe un rifiuto dentro
 * simulateAutomation, e non c'è ancora.
 * I rami non valutabili dell'evaluator non sono la stessa cosa e non vanno
 * promossi a diagnostica: sono la rete del MOTORE, che girerà senza UI davanti,
 * per il catalogo che cambia sotto un'automazione già pubblicata. Dei sette
 * motivi, i due che dipendono dal RECORD — campo illeggibile, oggetto non più
 * automatizzabile — sono comunque a video riga per riga, che è dove variano.
 *
 * Layout come il builder / detail page: .builder-layout a due colonne a tutta
 * altezza (builder-main scrollabile + builder-side fissa con border-left).
 * Sinistra: hero risultato, 3 stat, tabella record intercettati.
 * Destra (fascia fissa): azioni previste, warning, errori.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { formatNanos } from '@crm/shared';
import type { TFunction } from 'i18next';
import { useToast } from '../../components/useToast';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { extractError } from '../../store/thunkHelpers';
import { automationApi } from './automationApi';
import { actionIcon, DEFAULT_ACTION_ICON } from './automationIcons';
import { exclusionReasons } from './automationExclusionReasons';
import { formatScalar, summarizeConditionParts, SUMMARY_PLACEHOLDER } from './automationSummary';
import {
  fetchAutomation,
  fetchAutomationActionTypes,
  fetchAutomationCategories,
  fetchAutomationConditionTypes,
} from './automationThunks';
import { STATUS_CHIP_COLOR } from './automationTypes';
import type {
  AutomationConditionGroup,
  AutomationSimulation as AutomationSimulationResult,
  AutomationSimulationRecord,
  AutomationViolation,
  CatalogField,
} from './automationTypes';

// Quanti record proporre di esaminare all'apertura. Stesso valore che il server applica quando il
// parametro manca, ma qui non governa nulla: è solo il numero che il campo mostra per primo, e chi
// lo cancella non ottiene il default — ottiene un campo invalido, perché una richiesta senza taglia
// da questa pagina non parte.
const DEFAULT_RECORD_LIMIT = 50;

// L'intervallo ammesso dal campo, gemello di DEFAULT_SIMULATION_LIMIT/MAX_SIMULATION_LIMIT lato
// server. Che sia scritto in due posti è voluto e non è una svista da sanare: il server tiene il suo
// tetto perché deve difendersi da QUALUNQUE client, questo lo ripete perché chi compila il campo se
// ne accorga PRIMA di premere — altrimenti scriverebbe 5000, otterrebbe una risposta su 1000 e
// nessuno glielo direbbe. Sono due scopi diversi che portano allo stesso numero; se il
// server alzasse il tetto senza che questo lo segua, il campo resterebbe più severo del necessario,
// che è il verso innocuo del disallineamento.
const MIN_RECORD_LIMIT = 1;

const MAX_RECORD_LIMIT = 1000;

// Cella senza valore. Copre due casi che il DTO distingue ma che a video si somigliano — campo
// vuoto sul record, oppure valore mai letto perché la condizione non era valutabile — e li rende
// allo stesso modo: la distinzione la porta il `reasonKey`, che ha il proprio posto nel motivo
// dell'esclusione, non nella griglia dei valori.
const EMPTY_CELL = '—';

// Quante condizioni elencare nel pannello degli avvisi. Estratto e non elenco completo: la fascia
// destra è larga quanto un riquadro, e un'automazione con dieci condizioni la farebbe scorrere
// portandosi dietro le azioni previste. Il taglio è innocuo perché l'ordine è per frequenza — restano
// quelle che stringono di più — e quante ne restano fuori lo dice l'ultima riga, invece di sparire in
// silenzio.
const WARN_LIMIT = 5;

/**
 * Il valore di UN record su UNA colonna. La riga non arriva impaginata: porta l'esito di ogni
 * condizione, e la cella si trova cercando quella che nomina il campo della colonna.
 *
 * Lo stesso campo può comparire in più condizioni — l'utente lo ha messo in due gruppi — e si
 * prende la prima: è lo stesso record, quindi il valore letto è per forza identico.
 *
 * Rende con `formatScalar`, lo stesso del riepilogo del builder: sugli ENUM il DTO porta il valore
 * tecnico (`300`) e la colonna porta gli `enumValues` con cui nominarlo ("Inviato"). Usare due
 * formattatori diversi vorrebbe dire mostrare il codice qui e l'etichetta là, nella stessa pagina.
 */
function cellValue(record: AutomationSimulationRecord, column: CatalogField): string {
  const condition = record.conditions.find((c) => c.fieldName === column.fieldName);

  return formatScalar(column, condition?.actualValue) ?? EMPTY_CELL;
}

/**
 * Una condizione del blocco SE con quante volte il campione NON l'ha soddisfatta.
 *
 * Il testo è lo stesso della colonna "Non soddisfatte" — `summarizeConditionParts`, il formattatore
 * del riepilogo del builder — perché è la stessa condizione detta nello stesso posto: due frasi
 * diverse per la stessa regola, nella stessa schermata, si leggerebbero come due regole.
 */
interface UnmetCondition {
  automationConditionId: number;
  count: number;
  text: string;
  value?: string;
}

/**
 * Le condizioni non soddisfatte del campione, dalla più frequente alla meno.
 *
 * Aggregato del solo client: la risposta porta già l'esito di OGNI condizione su OGNI record, quindi
 * questa è una lettura diversa di dati che la pagina ha in mano — non un dato in più da chiedere.
 * La tabella li mostra per RIGA, rispondendo a "perché questo record è escluso"; qui si guarda la
 * stessa matrice per COLONNA, che risponde a una domanda che la tabella non pone: quale condizione
 * sta stringendo. Su un campione grande la prima si legge un record alla volta, la seconda a colpo
 * d'occhio.
 *
 * Si contano le occorrenze su TUTTI i record esaminati, non sui soli esclusi. Una condizione può
 * cadere anche su un record idoneo — i gruppi sono in OR, e ne basta un altro soddisfatto — e
 * scartare quei casi darebbe un conteggio più basso del numero di volte in cui quella condizione ha
 * davvero detto di no.
 *
 * Il conteggio è per CONDIZIONE e non per campo: lo stesso campo messo in due gruppi con soglie
 * diverse sono due regole distinte, e sommarle nasconderebbe quale delle due sta stringendo.
 */
function unmetConditions(
  records: AutomationSimulationRecord[],
  groups: AutomationConditionGroup[],
  conditionFields: CatalogField[],
  t: TFunction,
): UnmetCondition[] {
  // Catalogo non ancora arrivato: nessuna riga, invece di un elenco di segnaposto. Stessa scelta di
  // `exclusionReasons`, e per la stessa ragione — una lista di puntini con accanto dei numeri veri
  // si legge peggio di una lista assente.
  if (conditionFields.length === 0) return [];

  const counts = new Map<number, number>();

  records.forEach((record) =>
    record.conditions.forEach((condition) => {
      if (condition.satisfied) return;

      counts.set(
        condition.automationConditionId,
        (counts.get(condition.automationConditionId) ?? 0) + 1,
      );
    }),
  );

  // Le condizioni CONFIGURATE, appiattite dai gruppi: servono a nominare ciò che l'esito identifica
  // col solo id. Appiattire è lecito perché qui il gruppo non conta — si guarda quanto una regola
  // stringe, non in quale ramo OR viva.
  const configured = groups.flatMap((group) => group.conditions);

  return [...counts.entries()]
    .map(([automationConditionId, count]) => {
      const condition = configured.find(
        (c) => c.automationConditionId === automationConditionId,
      );
      const parts = condition ? summarizeConditionParts(condition, conditionFields, t) : null;

      return {
        automationConditionId,
        count,
        text: parts?.subject ?? SUMMARY_PLACEHOLDER,
        value: parts?.value,
      };
    })
    .sort((a, b) => b.count - a.count);
}

export default function AutomationSimulation() {
  // `i18n.language` serve a formattare la durata nel locale di chi legge, come fa il monitoraggio:
  // gli underscore di Liferay (it_IT) vanno convertiti nei trattini dei BCP 47 (it-IT).
  const { i18n, t } = useTranslation();
  const locale = i18n.language.replace(/_/g, '-');
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { id } = useParams();
  const automationDefinitionId = Number(id);
  // Il "torna indietro" va alla view SOLA LETTURA del ramo giusto: /automations/templates/:id per un
  // template (path sotto /templates/), /automations/:id per un'automazione.
  const isTemplateRoute = useLocation().pathname.startsWith('/automations/templates/');
  const backTo = isTemplateRoute ? `/automations/templates/${id}` : `/automations/${id}`;

  // Il record, letto da `byId` e non da `currentItem`: questa pagina lo consulta soltanto, e la
  // corsia condivisa `currentItem` la contendono le schermate che ci scrivono (il builder la azzera
  // all'uscita). Stessa scelta del monitoraggio, che è l'altra pagina di sola consultazione per id.
  const automation = useAppSelector((s) => s.automations.byId[automationDefinitionId]);
  // Categorie: servono per i COLORI del chip. Il record porta `automationCategoryName` denormalizzato
  // — che basta all'elenco, dove il chip non è colorato — ma non backColor/fontColor, che vivono solo
  // sulla categoria. Da qui la seconda fetch, la stessa che fa il builder.
  const categories = useAppSelector((s) => s.automations.categories);
  const category = categories.find(
    (c) => c.automationCategoryId === automation?.automationCategoryId,
  );

  useEffect(() => {
    if (!automationDefinitionId) return;
    dispatch(fetchAutomation(automationDefinitionId));
  }, [automationDefinitionId, dispatch]);

  useEffect(() => {
    dispatch(fetchAutomationCategories());
  }, [dispatch]);

  // Catalogo dei tipi azione: serve SOLO per l'icona di ciascuna riga del pannello. Il record porta
  // codice e titolo delle proprie azioni ma non l'icona, che vive sul tipo — stessa risoluzione che
  // fanno builder e modale. Con la guardia sulla cache, come il builder: il catalogo è per className
  // e non cambia mentre si simula.
  // Parte appena il className è noto e non all'esecuzione: il catalogo si può chiedere solo DOPO che
  // il record è arrivato, e chi esegue subito vedrebbe altrimenti un istante di icone generiche —
  // `actionIcon` cade sul fallback quando il tipo non è ancora in cache.
  const actionTypesByClassName = useAppSelector((s) => s.automations.actionTypesByClassName);
  const className = automation?.className;

  useEffect(() => {
    if (!className || actionTypesByClassName[className]) return;

    dispatch(fetchAutomationActionTypes(className));
  }, [actionTypesByClassName, className, dispatch]);

  const actionTypes = className ? (actionTypesByClassName[className] ?? []) : [];

  // I gruppi di condizioni servono a ricostruire, dagli id, a quale ramo OR appartiene una
  // condizione caduta: è l'unica cosa che la risposta non porta, e la pagina ce l'ha già senza
  // chiedere nulla in più.
  const conditionGroups = automation?.conditionGroups ?? [];

  // Catalogo dei campi filtrabili: serve a raccontare la condizione che ha escluso un record —
  // etichetta del campo, operatore con la sua parola o il suo simbolo, valore configurato. È lo
  // stesso catalogo che carica il builder, con la stessa guardia sulla cache: è per className e non
  // cambia mentre si simula.
  // Le `columns` della risposta non bastano: arrivano senza `operators`, perché intestano una
  // colonna e non servono a scegliere un confronto. Prenderlo da qui evita di duplicare nei locales
  // le dieci etichette degli operatori, che il backend già dichiara e localizza.
  const conditionTypesByClassName = useAppSelector((s) => s.automations.conditionTypesByClassName);

  useEffect(() => {
    if (!className || conditionTypesByClassName[className]) return;

    dispatch(fetchAutomationConditionTypes(className));
  }, [className, conditionTypesByClassName, dispatch]);

  const conditionFields = className ? (conditionTypesByClassName[className] ?? []) : [];

  // ─── Verdetto sulla configurazione SALVATA ────────────────────────────────────────────────────
  // Chiesto all'apertura, e non è una ripetizione del cancello del builder: quello spegne il PROPRIO
  // pulsante, ma questa pagina è una rotta, e chi la digita o la riapre da un segnalibro lo
  // scavalca. Qui la domanda si rifà, e la si fa sul RECORD — `getValidation` prende un id, non un
  // payload, quindi giudica ciò che la simulazione poi leggerà davvero e non una ricostruzione.
  // `null` = non si sa ancora (in volo). Come nel builder, il non sapere NON vale come verde: il
  // pulsante resta spento, perché lasciar simulare una configurazione che non si è potuta giudicare
  // è esattamente ciò che si sta impedendo.
  // Il fallimento della chiamata è uno stato SUO e non si confonde col `null`: un pulsante spento
  // per sempre senza nulla a video si legge come pagina rotta, e va detto che non si è potuto
  // verificare — cosa diversa dall'aver verificato e trovato problemi.
  const [violations, setViolations] = useState<AutomationViolation[] | null>(null);
  const [validationFailed, setValidationFailed] = useState(false);

  // Niente thunk, come per `simulate`: l'esito vive quanto la schermata e nessun altro lo consuma.
  // Lo slice ha già una corsia `violations`, ma è del BUILDER e parla del payload a video — scriverci
  // da qui vorrebbe dire far comparire nel pannello di un'altra schermata un verdetto su un altro
  // soggetto. Il flag di annullamento evita il set su componente smontato quando si esce prima che
  // la risposta arrivi.
  useEffect(() => {
    if (!automationDefinitionId) return;

    let cancelled = false;

    automationApi
      .getValidation(automationDefinitionId)
      .then((result) => {
        if (!cancelled) setViolations(result);
      })
      .catch(() => {
        if (!cancelled) setValidationFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [automationDefinitionId]);

  // Verde vero: si è chiesto E non è tornato nulla. `violations === null` (in volo o mai chiesto) e
  // la lista piena cadono entrambi qui, ed è voluto — stessa forma di `isValidated` nel builder.
  const isValidated = violations !== null && violations.length === 0;
  // Perché la pagina non può eseguire, quando non può: i due casi si rendono con testi diversi, e
  // sono distinti sul serio — "l'automazione ha questi problemi" e "non ho potuto controllare" non
  // chiedono la stessa cosa a chi legge.
  const hasViolations = violations !== null && violations.length > 0;
  const isBlocked = hasViolations || validationFailed;

  // Le azioni disattivate non scattano, quindi non compaiono: il pannello dice cosa SUCCEDEREBBE.
  const enabledActions = (automation?.actions ?? []).filter((action) => action.enabled);

  // Quanti record esaminare. Testo e non numero: un <input type="number"> controllato su uno stato
  // numerico non lascia svuotare il campo per riscriverlo (la stringa vuota non è un numero), e
  // costringe a rappresentare "in mezzo alla digitazione" come NaN. Si tiene il grezzo e si converte
  // al momento di eseguire.
  const [recordLimit, setRecordLimit] = useState(String(DEFAULT_RECORD_LIMIT));
  // `Number` e non `Number.parseInt`: il valore di un input numerico non è "solo cifre" ma un valid
  // floating-point number secondo la specifica HTML, quindi ci passano l'esponenziale e i decimali —
  // e `parseInt` li TRONCA al primo carattere non numerico invece di rifiutarli. Con `parseInt` chi
  // digitava `1e3` (la `e` nei campi numerici i browser la lasciano scrivere) otteneva 1: dentro
  // l'intervallo, pulsante acceso, simulazione su un record solo mentre il campo ne mostrava mille.
  // `Number` legge l'intera stringa — `1e3` è 1000, cioè quel che si intendeva — e `isInteger` scarta
  // i decimali mandandoli in errore anziché arrotondarli di nascosto.
  const parsedLimit = Number(recordLimit);
  const isLimitValid =
    Number.isInteger(parsedLimit) &&
    parsedLimit >= MIN_RECORD_LIMIT &&
    parsedLimit <= MAX_RECORD_LIMIT;
  // Il campo si tinge di rosso solo se c'è scritto qualcosa di sbagliato, non quando è VUOTO: chi ha
  // appena cancellato per riscrivere non ha ancora commesso un errore, e segnalarglielo a metà della
  // digitazione è rumore. Il pulsante intanto resta spento in entrambi i casi — è `isLimitValid` a
  // governarlo, e la stringa vuota non lo accende: `Number('')` è 0, che sta sotto il minimo.
  const isLimitOutOfRange = recordLimit.trim() !== '' && !isLimitValid;

  // La taglia con cui si è ESEGUITO, distinta da quella scritta nel campo: `null` finché non si
  // esegue. Ritoccare il campo non tocca i risultati — restano quelli dell'ultimo run — ed è questa
  // a etichettarli nell'hero. Senza il secondo stato l'hero leggerebbe il campo e finirebbe per
  // dichiarare i numeri di un campione con la taglia di un altro.
  const [ranLimit, setRanLimit] = useState<number | null>(null);
  // L'esito del run. Vive accanto a `ranLimit` e non al suo posto perché i due dicono cose diverse:
  // quanti record si sono CHIESTI e quanti se ne sono OTTENUTI. La differenza fra i due è
  // informazione — il sito ne ha meno, o i permessi ne hanno tolti — e tenerne uno solo la
  // cancellerebbe.
  // Alias sull'import: il componente si chiama già `AutomationSimulation`, e il tipo omonimo del DTO
  // gli si scontrerebbe contro nello stesso file.
  const [simulation, setSimulation] = useState<AutomationSimulationResult | null>(null);
  const hasRun = ranLimit !== null;
  const [running, setRunning] = useState(false);
  // Derivato e non trasportato: è la sottrazione fra due numeri che il server manda già, e un terzo
  // campo per un dato ricavabile darebbe due sorgenti da tenere d'accordo. Sta QUI, sotto lo stato
  // che legge: più su finirebbe prima della dichiarazione di `simulation`, e una const letta prima
  // della propria inizializzazione non è undefined — lancia, e il componente non renderizza affatto.
  const excludedRecords = simulation
    ? simulation.candidateRecords - simulation.eligibleRecords
    : 0;

  // L'estratto del pannello avvisi. Come `excludedRecords`, è una LETTURA dell'esito e non un dato a
  // sé: nulla da chiedere al server, che ha già mandato l'esito di ogni condizione su ogni record.
  // Ricalcolato a ogni render invece che memoizzato — scorre record × condizioni, cioè lo stesso
  // ordine di grandezza della tabella che sta qui accanto e che si ridisegna comunque.
  const unmet = simulation
    ? unmetConditions(simulation.records, conditionGroups, conditionFields, t)
    : [];

  // Chiama `automationApi` direttamente invece di passare da un thunk: non c'è nulla da mettere
  // nello store — l'esito vive quanto la schermata e si ricalcola a comando — e un thunk
  // aggiungerebbe un caso allo slice per uno stato che nessun altro consuma.
  // `extractError` e non `String(err)`: bypassando il thunk, qui arriva l'AxiosError grezzo, che
  // stringificato darebbe "Request failed with status code 403" invece del motivo. Altrove nei
  // componenti `String(err)` basta perché il thunk ha già estratto il messaggio.
  const handleRun = async () => {
    if (!automationDefinitionId || running || !isLimitValid || !isValidated) return;
    setRunning(true);
    try {
      const response = await automationApi.simulate(automationDefinitionId, parsedLimit);
      setSimulation(response);
      setRanLimit(parsedLimit);
    } catch (err) {
      toast({ severity: 'error', message: t('common.error', { message: extractError(err) }) });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="builder-layout">
      {/* LEFT — colonna scrollabile */}
      <div className="builder-main">
        {/* Il testo nomina la DESTINAZIONE, non l'origine: si torna a un template o a un'automazione,
            e sono due cose diverse per chi legge. Stessa coppia chiave/rotta del link "indietro" del
            builder, che sceglie sullo stesso booleano. */}
        <Link to={backTo} className="text-sm text-primary builder-back">
          &larr;{' '}
          {t(
            isTemplateRoute
              ? 'automation.simulation.backToTemplate'
              : 'automation.simulation.backToAutomation',
          )}
        </Link>

        {/* Barra titolo + azioni. Il nome è quello del RECORD: finché non è arrivato si lascia vuoto
            anziché mettere un segnaposto, come fa il monitoraggio — una riga che compare è meno
            fastidiosa di una che cambia sotto gli occhi. */}
        <div className="builder-topbar">
          <div className="builder-topbar__title">
            {/* Stessa impalcatura della view del record: `builder-titlefields` incolonna la riga di
                testata e la descrizione, `builder-titlerow` mette badge e nome sulla stessa riga e li
                centra verticalmente. Senza i due wrapper il badge ereditava l'`align-items: flex-start`
                di `__title` e non si allineava al nome. */}
            <div className="builder-titlefields">
              <div className="builder-titlerow">
                {/* Badge = stato corrente + versione, e viene PRIMA del nome come là. Colore dalla
                    mappa dei chip di elenco e gallery (STATUS_CHIP_COLOR), che per draft e archived
                    dice 'default', cioè nessuna variante e classe base.
                    Condizionato al record e non a un default locale: qui non si crea nulla, quindi
                    finché non è arrivato non esiste uno stato da mostrare. */}
                {automation && (
                  <span
                    className={`builder-badge${
                      STATUS_CHIP_COLOR[automation.status] !== 'default'
                        ? ` builder-badge--${STATUS_CHIP_COLOR[automation.status]}`
                        : ''
                    }`}
                  >
                    {t(`automationsInsights.list.statuses.${automation.status}`)} · v
                    {automation.version}
                  </span>
                )}
                {/* Categoria: sulla view del record è un ChipSelect, qui uno <span> con la stessa
                    classe e gli stessi colori inline — non c'è nulla da scegliere, e un dropdown che
                    si apre su una pagina di sola consultazione prometterebbe una modifica che non
                    esiste. Reso solo se la categoria si risolve: là il chip senza categoria mostra il
                    placeholder "scegli una categoria", che qui sarebbe un invito a nulla. */}
                {category && (
                  <span
                    className="builder-catchip"
                    style={{ backgroundColor: category.backColor, color: category.fontColor }}
                  >
                    {category.name}
                  </span>
                )}
                {/* `builder-titleinput` su un div e non su un input: la classe porta la tipografia del
                    nome (700, font-size-lg, flex:1) e i suoi reset servono a spegnere l'aspetto di
                    campo, che qui non c'è già. Il nome non si modifica da questa pagina. */}
                <div className="builder-titleinput">{automation?.name ?? ''}</div>
              </div>
              {/* Descrizione, sotto la riga come là. Omessa quando manca invece di lasciare un
                  elemento vuoto: la textarea del builder occupa comunque una riga perché è un campo
                  che si può compilare, qui sarebbe spazio senza contenuto né scopo. */}
              {automation?.description && (
                <div className="builder-descinput">{automation.description}</div>
              )}
            </div>
          </div>
          <div className="builder-topbar__modes">
            {/* Quanti record esaminare. `type="number"` per la tastiera numerica su mobile e le
                frecce di incremento; `min`/`max` fermano le frecce agli estremi, ma da soli non
                bastano — su un campo di testo libero il browser non impedisce di DIGITARE fuori
                intervallo, segnala soltanto. Il divieto vero è il pulsante spento, che è dove la
                regola era già: prima diceva "un intero positivo", ora dice "un intero nell'arco
                ammesso". Nessun valore viene riscritto sotto le dita di chi digita: 5000 resta 5000
                e non si esegue, invece di diventare 1000 senza che nessuno lo abbia chiesto.
                L'intervallo NON è scritto qui: né in un `helperText`, che allungherebbe il campo di
                una riga e farebbe crescere la barra su quella, né nell'etichetta, che dentro un
                outlined sta nel bordo e ne detta quindi la larghezza minima — con "Record da
                esaminare (1-1000)" la scatola arrivava a 210px per contenere un numero di due o tre
                cifre. L'etichetta si è ridotta al sostantivo e gli estremi sono scesi nel sottotitolo
                dello stato vuoto, che è il testo che si legge PRIMA di compilare ed è largo quanto
                la pagina; nel `title` qui sotto restano a portata di mouse anche dopo, quando quel
                sottotitolo non c'è più. Entrambi i posti non occupano un pixel del layout, che è la
                condizione da cui non si può uscire.
                TextField MUI e non un <input> con etichetta accanto: l'etichetta sta DENTRO il campo
                come in ogni altra form del progetto, e con lei arrivano il collegamento accessibile
                e l'altezza propria del controllo — che l'accoppiata label+input grezzo non aveva,
                lasciando il testo dell'etichetta stirato in cima alla barra.
                Niente `form-field`, che è la classe dei campi IMPILATI: porta un margin-bottom
                pensato per la colonna di una form e qui sfalserebbe il campo rispetto al pulsante.
                La larghezza è locale perché locale è il motivo — quattro cifre, le frecce dello
                spinner e un'etichetta di una parola — e non merita una classe che nessun altro
                riuserebbe. */}
            <TextField
              id="simulation-limit"
              label={t('automation.simulation.limitLabel')}
              type="number"
              size="small"
              value={recordLimit}
              onChange={(e) => setRecordLimit(e.target.value)}
              error={isLimitOutOfRange}
              slotProps={{
                htmlInput: {
                  min: MIN_RECORD_LIMIT,
                  max: MAX_RECORD_LIMIT,
                  // Il bordo rosso dice CHE c'è un errore, non quale: `error` fa impostare a MUI
                  // `aria-invalid`, ma senza `helperText` non c'è testo a cui puntare. Il `title`
                  // colma il buco senza occupare spazio — è il tooltip nativo del browser e non
                  // entra nel flusso — e serve soprattutto DOPO il primo run, quando lo stato vuoto
                  // che dichiara l'intervallo non è più a video.
                  title: t('automation.simulation.limitTitle', {
                    min: MIN_RECORD_LIMIT,
                    max: MAX_RECORD_LIMIT,
                  }),
                },
              }}
              sx={{ width: 100 }}
            />
            {/* Eseguire FISSA la taglia del campione nel risultato: da qui in poi l'hero parla di
                questa, anche se il campo cambia. */}
            {/* Tre condizioni, non due: alla taglia valida e al run non in corso si aggiunge il
                verde sulla configurazione SALVATA. La guardia sta anche dentro `handleRun`, perché
                un pulsante disabilitato è una cortesia dell'interfaccia e non un divieto — qui
                l'azione parte da un click, ma la regola vive nella funzione che la esegue. */}
            <Button
              size="small"
              variant="contained"
              disabled={running || !isLimitValid || !isValidated}
              onClick={handleRun}
            >
              ↻ {t('automation.simulation.run')}
            </Button>
          </div>
        </div>

        {/* Prima del run: lo stato vuoto della casa (`empty-state`, come l'elenco task) — centrato.
            Il vuoto assoluto si sarebbe letto come una pagina rotta anziché in attesa.
            Tre blocchi con compiti distinti: il titolo dice a COSA SERVE la schermata invece di
            constatare che non si è ancora fatto niente, il corpo spiega cosa si otterrà e chiude
            rassicurando che nulla viene eseguito né salvato — è la domanda che uno si fa davanti a
            un pulsante su un'automazione attiva — e l'ultima riga dà l'istruzione operativa.
            L'INTERVALLO ammesso dal campo vive qui perché nella barra non c'è più: è il testo che si
            legge prima di compilare, e qui lo spazio non costa mentre lassù ogni carattere allargava
            la scatola del numero. Che sparisca dopo il primo run è coerente col momento in cui
            serve; a ricordarlo restano il bordo rosso e il pulsante spento. */}
        {!hasRun && !isBlocked && (
          <div className="empty-state">
            <p className="text-lg font-semibold">{t('automation.simulation.emptyTitle')}</p>
            <p>{t('automation.simulation.emptyBody')}</p>
            <p>
              {t('automation.simulation.emptyHint', {
                min: MIN_RECORD_LIMIT,
                max: MAX_RECORD_LIMIT,
              })}
            </p>
          </div>
        )}

        {/* Configurazione che non regge, oppure verdetto che non si è potuto ottenere: al posto
            dello stato vuoto — che invita a premere un pulsante ormai spento — si dice perché non
            si parte. Stesso contenitore `empty-state`, così la pagina non cambia forma fra i due
            casi e non serve markup nuovo.
            Sostituisce e non affianca: due blocchi insieme direbbero "ecco a cosa serve questa
            schermata" e "questa schermata non può funzionare" uno sotto l'altro.
            Nessun blocco per il caso in volo: dura una manciata di millisecondi, e riempirlo con un
            terzo messaggio farebbe lampeggiare la pagina a ogni apertura. In quella finestra si
            vede lo stato vuoto normale, col solo pulsante spento. */}
        {!hasRun && isBlocked && (
          <div className="empty-state">
            <p className="text-lg font-semibold">
              {t(
                hasViolations
                  ? 'automation.simulation.blockedTitle'
                  : 'automation.simulation.unverifiedTitle',
              )}
            </p>
            <p>
              {t(
                hasViolations
                  ? 'automation.simulation.blockedBody'
                  : 'automation.simulation.unverifiedBody',
              )}
            </p>
            {/* I motivi in una scatola tinta e allineata a sinistra, non come paragrafi in mezzo
                agli altri: nello stato vuoto il testo è grigio e centrato, e lì dentro l'elenco che
                spiega perché il pulsante è spento si confondeva col testo di contorno.
                Condizionato a `hasViolations` e non al solo `violations`: sul ramo "non verificato"
                la lista è null, e senza la guardia resterebbe a video una scatola rossa vuota.
                Un paragrafo per violazione, col proprio marcatore, invece di un <ul>: i messaggi
                arrivano già tradotti dal server — le chiavi le ha solo lui — e si leggono come frasi
                intere, per cui il bullet duplicherebbe il segno che c'è già.
                Le violazioni non hanno id: la lista è ricostruita per intero a ogni risposta e non è
                né riordinabile né editabile, quindi blocco + indice è una key stabile quanto serve.
                Stessa scelta del pannello Validazione del builder. */}
            {hasViolations && (
              <div className="empty-state__errors">
                {violations?.map((violation, index) => (
                  <p key={`${violation.block}-${index}`}>⚠ {violation.message}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {hasRun && (
          <>
            <div className="sim-hero">
              {/* La taglia qui è quella con cui si è ESEGUITO, non quella nel campo: è l'etichetta dei
                  numeri sotto, e deve restare fedele a loro anche quando il campo va altrove.
                  Il testo dice "richiesti" e non "ultimi N record" perché N è ciò che si è CHIESTO,
                  non ciò che si è ottenuto: la stat qui sotto mostra il campione reale, che su un
                  sito piccolo o con permessi parziali è più basso. Con la vecchia formulazione la
                  pagina affermava in grande di aver esaminato 100 record mentre poco sotto ne
                  dichiarava 12. */}
              <div className="sim-hero__h">
                {t('automation.simulation.resultLabel', { count: ranLimit })}
              </div>
              {/* Quante volte l'automazione sarebbe scattata: una per record idoneo, che è la stessa
                  cosa detta col verbo invece che col sostantivo della stat. In grande perché è la
                  risposta alla domanda per cui si simula. */}
              <div className="sim-hero__big">
                {t('automation.simulation.resultBig', { count: simulation?.eligibleRecords ?? 0 })}
              </div>
              {/* Il sottotitolo si compone di due frasi indipendenti, ciascuna col proprio conteggio:
                  i18next declina il plurale su UN solo `count` per chiave, quindi due numeri nella
                  stessa stringa non potrebbero essere entrambi corretti al singolare.
                  La frase sugli esclusi compare SEMPRE, zero compreso: "nessun record escluso" è
                  un'informazione — dice che il blocco SE non ha scartato nulla — mentre l'assenza
                  della riga lascerebbe solo il dubbio che il dato manchi. Le tre cardinalità le
                  distingue la chiave con i suffissi _zero/_one/_other, non un if qui dentro.
                  Non si dice ancora PERCHÉ siano esclusi, ma il dato non manca più: sta nella
                  tabella qui sotto, riga per riga. Qui resterebbe comunque fuori posto — l'hero
                  riassume il campione, e una causa vale per un record solo. */}
              <div className="sim-hero__s">
                {t('automation.simulation.resultActions', {
                  count: simulation?.plannedActions ?? 0,
                })}{' '}
                {t('automation.simulation.resultExcluded', { count: excludedRecords })}
              </div>
            </div>

            <div className="simstats">
              {/* I tre conteggi, tutti calcolati dal server: intercettati è il campione raccolto,
                  idonei quanti superano il blocco SE, azioni previste gli idonei moltiplicati per
                  le azioni ATTIVE. Nessuno dei tre è più un segnaposto, e tutti e tre si muovono al
                  variare del campione.
                  Optional chaining senza `?? 0`: l'accesso protetto serve perché `simulation` e
                  `hasRun` sono due stati distinti e TypeScript non sa che si popolano insieme, ma un
                  default a zero direbbe "nessun record" — una risposta plausibile e falsa — nel caso
                  in cui il campo mancasse davvero. Meglio nessun numero che uno inventato:
                  `undefined` non viene reso, e una casella vuota si nota. */}
              <div className="simstat">
                <div className="simstat__n">{simulation?.candidateRecords}</div>
                <div className="simstat__l">{t('automation.simulation.statIntercepted')}</div>
              </div>
              <div className="simstat">
                <div className="simstat__n">{simulation?.eligibleRecords}</div>
                <div className="simstat__l">{t('automation.simulation.statEligible')}</div>
              </div>
              <div className="simstat">
                <div className="simstat__n">{simulation?.plannedActions}</div>
                <div className="simstat__l">{t('automation.simulation.statActions')}</div>
              </div>
              {/* Durata DELLA SIMULAZIONE, non di un'esecuzione: qui non si esegue niente, e
                  l'etichetta lo dice per non farla scambiare per il costo di un run vero — dove
                  sarebbero gli invii mail e le creazioni attività a pesare, non la valutazione.
                  Serve a vedere quanto costa il dry-run al crescere del campione. */}
              <div className="simstat">
                <div className="simstat__n">
                  {simulation ? formatNanos(simulation.simulationDuration, locale) : null}
                </div>
                <div className="simstat__l">{t('automation.simulation.statDuration')}</div>
              </div>
              {/* I tre tempi della VALUTAZIONE, nello stesso riquadro perché si leggono insieme: un
                  massimo lontano dal minimo dice che qualche record costa più degli altri, e la media
                  da sola lo nasconderebbe. Misurano la sola valutazione, non la selezione — quindi
                  moltiplicati per i record non danno la durata totale qui accanto.
                  Il primo record paga di norma il riscaldamento delle cache dei finder, quindi un
                  massimo molto sopra la media al primo giro è atteso e non è un allarme.
                  Senza etichetta sotto, a differenza degli altri riquadri: tre righe più un titolo
                  facevano crescere in altezza tutta la fascia, e la griglia allinea i riquadri sul
                  più alto. Quel che il titolo diceva resta nel `title`, che è un tooltip e non
                  occupa spazio — le tre righe da sole direbbero "minimo/media/massimo" senza dire
                  di cosa. */}
              <div className="simstat" title={t('automation.simulation.statEvaluation')}>
                <div className="simstat__rows">
                  <div className="simstat__row">
                    <span>{t('automation.simulation.evalMin')}</span>
                    <b>{simulation ? formatNanos(simulation.minDuration, locale) : null}</b>
                  </div>
                  <div className="simstat__row">
                    <span>{t('automation.simulation.evalAverage')}</span>
                    <b>
                      {simulation ? formatNanos(simulation.averageDuration, locale) : null}
                    </b>
                  </div>
                  <div className="simstat__row">
                    <span>{t('automation.simulation.evalMax')}</span>
                    <b>{simulation ? formatNanos(simulation.maxDuration, locale) : null}</b>
                  </div>
                </div>
              </div>
            </div>

            {/* La tabella compare solo se c'è qualcosa da mettere in griglia: su campione vuoto la
                sola intestazione sarebbe un guscio, e quanti record sono stati esaminati lo dicono
                già l'hero e le stat qui sopra.
                Le intestazioni arrivano TUTTE dal server, in due serie che rispondono a due
                domande diverse. Le FISSE le dichiara l'entità (`fixedColumns`) e dicono quale
                record è: cambiano fra Person, Business e Quotation ma non fra due automazioni sulla
                stessa entità. Le DINAMICHE le decide l'automazione (`columns`), sono i campi che il
                blocco SE usa davvero e dicono perché quel record è stato intercettato o scartato.
                Scritte qui dentro restano solo le ultime due, esito e non soddisfatte.
                Un'automazione senza condizioni ha le dinamiche vuote e le fisse no: chi non filtra
                nulla non ha valori da mostrare, ma i suoi record hanno comunque un nome. */}
            {simulation && simulation.records.length > 0 && (
              <section className="builder-card">
                <div className="builder-card__head">{t('automation.simulation.examplesTitle')}</div>
                <table className="simtable">
                  <thead>
                    <tr>
                      {/* Le colonne FISSE, quelle che dicono QUALE record è. Non ce n'è nessuna
                          cablata qui — nemmeno l'ID: le dichiara il provider dell'entità, quindi
                          una tabella su Person intesta "Nome completo" dove una su Business
                          intesta "Azienda", e aggiungerne una non tocca questo file.
                          L'indice come key è stabile quanto serve: l'elenco arriva dal server, non
                          è riordinabile né filtrabile, e la sua lunghezza è la stessa per tutta la
                          durata di una risposta. */}
                      {simulation.fixedColumns.map((column, index) => (
                        <th key={index}>{column}</th>
                      ))}
                      {simulation.columns.map((column) => (
                        <th key={column.fieldName}>{column.label}</th>
                      ))}
                      <th>{t('automation.simulation.colOutcome')}</th>
                      {/* La motivazione ha una colonna sua, in fondo: sta accanto all'esito che
                          spiega, ed è l'unica che può contenere più righe. Intestazione fissa come
                          le altre due — c'è anche quando nessun record è escluso, perché una
                          colonna che compare e scompare farebbe ballare la tabella fra due
                          esecuzioni.
                          L'intestazione dice "non soddisfatte" e non "motivazione" perché le righe
                          qui sotto sono condizioni scritte in forma AFFERMATIVA: senza la cornice
                          si leggono come fatti sul record, cioè l'esatto contrario di quello che
                          sono. Il marcatore su ogni riga ripete la stessa cosa a livello di riga,
                          per chi ne legge una sola. */}
                      <th>{t('automation.simulation.colUnmetConditions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulation.records.map((record) => {
                      // Calcolati una volta per riga e non dentro il JSX: servono due volte —
                      // per decidere se l'elenco va reso e per renderlo — e su un record idoneo
                      // non si calcolano affatto, perché non c'è niente da spiegare.
                      const reasons = record.eligible
                        ? []
                        : exclusionReasons(record.conditions, conditionGroups, conditionFields, t);

                      return (
                        <tr key={record.recordId}>
                          {/* Le celle fisse, accoppiate alle intestazioni per POSIZIONE e non per
                              nome: le due liste nascono dallo stesso metodo del provider, chiamato
                              su record diversi della stessa entità, quindi hanno per costruzione
                              lunghezza e ordine identici.
                              La prima in grassetto perché per convenzione è l'identificativo — è
                              l'unica assunzione che questo file fa sull'elenco, ed è cosmetica: se
                              un provider mettesse altro al primo posto si vedrebbe in grassetto,
                              non si romperebbe nulla. */}
                          {record.fixedValues.map((value, index) => (
                            <td key={index}>{index === 0 ? <b>{value}</b> : value}</td>
                          ))}
                          {simulation.columns.map((column) => (
                            <td key={column.fieldName}>{cellValue(record, column)}</td>
                          ))}
                          {/* Esito: il verdetto arriva dal server e NON si ricalcola dalle
                              condizioni della riga, che sono in lista piatta e hanno perso il
                              confine fra i rami OR — il loro AND direbbe il falso su
                              un'automazione con più gruppi. */}
                          <td>
                            <span
                              className={`status-dot ${
                                record.eligible ? 'status-dot--ok' : 'status-dot--skip'
                              }`}
                            >
                              <span className="status-dot__d" />
                              {record.eligible
                                ? t('automation.simulation.eligible')
                                : t('automation.simulation.excluded')}
                            </span>
                          </td>
                          {/* Motivazione: una riga per gruppo caduto. La cella resta VUOTA sugli
                              idonei — non c'è niente da spiegare — e finché l'automazione e il
                              catalogo non sono in store, dove i motivi non si possono ancora
                              comporre: in entrambi i casi meglio una cella vuota di un segnaposto,
                              che si leggerebbe come "motivo sconosciuto". */}
                          <td>
                            {reasons.length > 0 && (
                              <ul className="simtable__reasons">
                                {reasons.map((reason) => (
                                  <li key={reason.group}>
                                    {/* Il numero di gruppo si scrive solo quando i gruppi caduti
                                        sono più d'uno: con un gruppo solo non c'è ambiguità da
                                        sciogliere, e un'intestazione che dice sempre "Gruppo 1"
                                        occuperebbe una riga per non dire nulla. */}
                                    {reasons.length > 1 && (
                                      <div className="simtable__reasons-group">
                                        {t('automation.conditions.reasonGroupHeader', {
                                          index: reason.group,
                                        })}
                                      </div>
                                    )}
                                    {/* Il valore in corsivo, staccato da quel che si confronta:
                                        in una colonna fitta è ciò che distingue la soglia dalla
                                        regola. Assente sulle condizioni non valutabili, dove la
                                        causa è tutta nel testo e non c'è nulla da staccare. */}
                                    {reason.conditions.map((condition) => (
                                      <div
                                        className="simtable__reasons-item"
                                        key={condition.automationConditionId}
                                      >
                                        {condition.text}
                                        {condition.value && (
                                          <>
                                            {' '}
                                            <i>{condition.value}</i>
                                          </>
                                        )}
                                      </div>
                                    ))}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            )}
          </>
        )}
      </div>

      {/* RIGHT — fascia destra fissa (come .detail-right). Anch'essa è risultato: dice cosa la
          simulazione FAREBBE e cosa la ostacola, quindi prima del run non ha nulla da dire e non
          compare. Niente "Attiva" qui dentro: attivare è un'azione sul record e vive sulla sua view,
          dove sta accanto a Modifica ed Elimina; averla anche qui la duplicava in una schermata che
          il record non lo cambia. */}
      {hasRun && (
        <aside className="builder-side">
          {/* Le azioni ATTIVE dell'automazione, una riga ciascuna, col numero di record su cui
              scatterebbero — che è lo stesso per tutte, perché ogni record idoneo le fa scattare
              tutte. Il totale della terza stat è appunto questo numero moltiplicato per le righe qui
              sotto.
              I dati vengono dal record già in store, non da una chiamata dedicata: `automation`
              porta le proprie azioni, e l'icona la risolve il catalogo dei tipi per `code`, la
              stessa mappa che usano builder e modale. Il titolo è quello che l'utente ha scritto,
              non l'etichetta del tipo: distingue due invii mail che il tipo non distinguerebbe.
              ATTENZIONE: il totale della terza stat lo conta il SERVER (eligibleRecords per le
              azioni attive), queste righe le conta il client filtrando `automation.actions`. Oggi
              coincidono per costruzione — il DTO porta le azioni dallo stesso finder che il server
              conta — ma sono due letture indipendenti: se un domani il numero non tornasse con le
              righe, la causa è qui. */}
          {enabledActions.length > 0 && (
            <div className="builder-side__box">
              <h3>{t('automation.simulation.plannedActions')}</h3>
              {enabledActions.map((action) => {
                const Icon = actionIcon(
                  actionTypes.find((a) => a.code === action.code)?.icon || DEFAULT_ACTION_ICON,
                );

                return (
                  <div className="builder-val" key={action.automationActionId}>
                    <span className="builder-val__ok">
                      <Icon fontSize="small" />
                    </span>
                    {/* `title` anche come attributo: il nome tronca con i puntini quando è lungo, e
                        il tooltip nativo è l'unico modo per leggerlo per intero senza allargare la
                        fascia, che ha larghezza fissa. */}
                    <span className="builder-val__name" title={action.title}>
                      {action.title}
                    </span>
                    <span className="builder-val__count">
                      {simulation?.eligibleRecords ?? 0}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Quali condizioni stanno stringendo, dalla più frequente. La tabella qui a sinistra
              legge la stessa matrice per RIGA — perché QUESTO record è escluso — e questo pannello
              la legge per COLONNA: quale regola dice di no più spesso. Sono due domande diverse, e
              la seconda su cinquanta righe non si risponde guardandole una per una.
              Nessun dato in più dal server: l'esito di ogni condizione su ogni record è già nella
              risposta della simulazione, e questo è un raggruppamento di quello. */}
          <div className="sim-panel sim-panel--warn">
            <div className="sim-panel__t">⚠ {t('automation.simulation.warnTitle')}</div>
            <div className="sim-panel__body">
              {/* Cosa sono i numeri qui sotto. Senza, un elenco di condizioni con accanto una cifra
                  si presta a due letture opposte — quanti record la soddisfano, quanti no — e sono
                  l'una il complemento dell'altra, quindi nessuna delle due si smaschera da sola
                  guardando i valori. Dice anche l'ORDINE, che altrimenti si scoprirebbe solo
                  notando che i numeri calano. */}
              <div className="sim-panel__sub">{t('automation.simulation.warnIntro')}</div>
              {unmet.length === 0 ? (
                t('automation.simulation.warnNone')
              ) : (
                <>
                  {unmet.slice(0, WARN_LIMIT).map((condition) => (
                    <div key={condition.automationConditionId}>
                      {/* Il conteggio in testa e in grassetto: è la ragione per cui la riga sta in
                          un elenco ordinato, e leggerlo in fondo costringerebbe a scorrere l'intera
                          frase per capire se quella regola conta. Il valore atteso in corsivo,
                          staccato da ciò che si confronta, come nella colonna della tabella. */}
                      <b>{condition.count}×</b> {condition.text}
                      {condition.value && (
                        <>
                          {' '}
                          <i>{condition.value}</i>
                        </>
                      )}
                    </div>
                  ))}
                  {unmet.length > WARN_LIMIT && (
                    <div>
                      {t('automation.simulation.warnMore', {
                        count: unmet.length - WARN_LIMIT,
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}